// src/pages/driver/DriversPage.js - Fixed styling and logic
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  IconButton
} from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import { getDrivers, deleteDriver } from '../../api/driverAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const DriversPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showEnabledOnly, setShowEnabledOnly] = useState(false);

  // Add state for delete dialog and selected driver
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const queryClient = useQueryClient();

  // Fixed: When showEnabledOnly is true, show only enabled drivers. When false, show all drivers.
  const { data: drivers, isLoading, isError, error } = useQuery({
    queryKey: ['drivers', showEnabledOnly],
    queryFn: () => {
      if (showEnabledOnly) {
        return getDrivers(true); // Show only enabled drivers
      } else {
        // To get all drivers (both enabled and disabled), we need to make two calls and combine them
        return Promise.all([
          getDrivers(true),  // Get enabled drivers
          getDrivers(false)  // Get disabled drivers
        ]).then(([enabledDrivers, disabledDrivers]) => {
          // Combine both arrays and remove duplicates based on DriverId
          const allDrivers = [...enabledDrivers, ...disabledDrivers];
          const uniqueDrivers = allDrivers.filter((driver, index, self) => 
            index === self.findIndex(d => d.DriverId === driver.DriverId)
          );
          return uniqueDrivers;
        });
      }
    }
  });

  // Add mutation for delete functionality
  const deleteMutation = useMutation({
    mutationFn: (driverId) => deleteDriver(driverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      setSnackbar({
        open: true,
        message: 'Driver deleted successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting driver: ${error.message}`,
        severity: 'error'
      });
    }
  });

  // Handler functions for delete dialog
  const handleDeleteClick = (driver) => {
    setDriverToDelete(driver);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (driverToDelete) {
      try {
        await deleteMutation.mutateAsync(driverToDelete.DriverId);
        setDeleteDialogOpen(false);
        setDriverToDelete(null);
      } catch (err) {
        // Error handled by mutation
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Toggle handler for enabled/all drivers
  const handleToggleEnabled = () => {
    setShowEnabledOnly(!showEnabledOnly);
  };

  // Filter drivers by search term
  const filteredDrivers = drivers?.filter((driver) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      driver.DriverFullName?.toLowerCase().includes(searchLower) ||
      driver.DriverEmail?.toLowerCase().includes(searchLower) ||
      driver.DriverPhone?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading drivers'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Drivers</Typography>
        {/* <Button 
          component={Link} 
          to="/drivers/new" 
          variant="contained" 
          startIcon={<AddIcon />}
        >
          Add Driver
        </Button> */}
      </Box>
      

      {/* Search Bar and Toggle - Fixed to match Companies page style */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              placeholder="Search drivers"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ backgroundColor: 'white' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} edge="end">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={showEnabledOnly}
                  onChange={handleToggleEnabled}
                  color="primary"
                />
              }
              label="Show enabled drivers only"
            />
          </Grid>
        </Grid>
      </Paper>

      {filteredDrivers && filteredDrivers.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No drivers found
          </Typography>
          <Button 
            component={Link} 
            to="/drivers/new" 
            variant="contained" 
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Add Your First Driver
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredDrivers && filteredDrivers.map((driver) => (
            <Grid item xs={12} md={6} lg={4} key={driver.DriverId}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" component="div" gutterBottom>
                      {driver.DriverFullName}
                    </Typography>
                    <Chip
                      label={driver.DriverEnabled ? "Enabled" : "Disabled"}
                      color={driver.DriverEnabled ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                  
                  {/* Driver Details */}
                  <Box sx={{ mt: 2 }}>
                    {driver.DriverEmail && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Email: {driver.DriverEmail}
                      </Typography>
                    )}
                    {driver.DriverPhone && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Phone: {driver.DriverPhone}
                      </Typography>
                    )}
                    {driver.DriverGroupId && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Group ID: {driver.DriverGroupId}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                
                {/* Action Buttons */}
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="View Driver Details">
                      <Button
                        component={Link}
                        to={`/drivers/${driver.DriverId}`}
                        variant="outlined"
                        color="primary"
                        startIcon={<VisibilityIcon />}
                        size="small"
                      >
                        View
                      </Button>
                    </Tooltip>
                    
                    <Tooltip title="Edit Driver">
                      <Button
                        component={Link}
                        to={`/drivers/${driver.DriverId}/edit`}
                        variant="outlined"
                        color="info"
                        startIcon={<EditIcon />}
                        size="small"
                      >
                        Edit
                      </Button>
                    </Tooltip>
                    
                    {/* Updated Delete Button */}
                    <Tooltip title="Delete Driver">
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(driver)}
                        size="small"
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* No results for search */}
      {drivers && drivers.length > 0 && 
       filteredDrivers && filteredDrivers.length === 0 && searchTerm && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No drivers found matching "{searchTerm}"
          </Typography>
          <Button 
            onClick={clearSearch}
            variant="outlined"
            sx={{ mt: 2 }}
          >
            Clear Search
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Driver</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{driverToDelete?.DriverFullName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriversPage;